import { awards } from "../../../../engine/awards";
import { benchPoints } from "../../../../engine/bench";
import { insights } from "../../../../engine/insights";
import { funBoard, mainBoard } from "../../../../engine/ranking";
import type { TournamentState } from "../../../../engine/types";
import { eventName, nameOf, pairNames } from "./common";

function cell(v: string | number): string {
  const text = String(v);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function line(cells: (string | number)[]): string {
  return cells.map(cell).join(",");
}

const yesNo = (b: boolean): string => (b ? "yes" : "no");
const round2 = (n: number): string => String(Math.round(n * 100) / 100);

/**
 * One row per game, then a blank line and the main board, then the fun board, the awards, the insights.
 * Real names throughout, each board with the player's nickname beside it.
 */
export function dayCsv(state: TournamentState): string {
  const nickname = (id: string): string => state.players[id]?.nickname ?? "";
  const fun = funBoard(state);
  const funOf = new Map(fun.map((f) => [f.id, f.funPoints]));
  const bench = benchPoints(state);
  const rows: string[] = [line(["round", "court", "team A", "team B", "score A", "score B", "capped", "event", "cursed by", "voided"])];
  for (const r of state.rounds) {
    for (const g of r.games) {
      rows.push(line([
        r.no,
        g.court,
        pairNames(state, g.a),
        pairNames(state, g.b),
        g.score ? g.score.a : "",
        g.score ? g.score.b : "",
        g.score ? yesNo(g.score.capped) : "",
        eventName(state, g.eventId) ?? "",
        g.cursedBy ? nameOf(state, g.cursedBy) : "",
        yesNo(g.voided),
      ]));
    }
  }
  rows.push("", "Main board");
  rows.push(line(["rank", "name", "nickname", "games", "wins", "points", "against", "average", "win %", "difference", "podium", "fun points"]));
  for (const m of mainBoard(state)) {
    rows.push(line([m.rank, m.name, nickname(m.id), m.games, m.wins, m.points, m.against, round2(m.avg), Math.round(m.winPct * 100), m.diff, yesNo(m.eligible), round2(funOf.get(m.id) ?? 0)]));
  }
  rows.push("", "Fun board");
  rows.push(line(["rank", "name", "nickname", "fun points", "event games", "event wins", "from bounties", "right picks", "wrong picks", "from picks"]));
  for (const f of fun) {
    const b = bench.get(f.id);
    rows.push(line([f.rank, f.name, nickname(f.id), round2(f.funPoints), f.eventGames, f.eventWins, b?.bounty ?? 0, b?.right ?? 0, b?.wrong ?? 0, b?.picks ?? 0]));
  }
  rows.push("", state.reveal.awards ? "Awards" : "Awards (not handed out, so not in the fun points)");
  rows.push(line(["title", "winners", "detail", "fun points each"]));
  for (const a of awards(state, "admin")) rows.push(line([a.title, a.winners.map((id) => nameOf(state, id)).join(" & "), a.detail, a.points]));
  rows.push("", "Insights");
  rows.push(line(["title", "detail"]));
  for (const i of insights(state, "admin")) rows.push(line([i.title, i.detail]));
  return rows.join("\r\n") + "\r\n";
}

/** The day's date in London, for file names: 2026-10-18. */
export function dayStamp(ms: number): string {
  return new Date(ms).toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

/** Build a Blob and click a temporary link to it. */
export function download(name: string, type: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
